import { useCallback, useState } from 'react';
import {
  FilterColumnType,
  Option,
  parseTokensBasicToAdvanced
} from '@blisspointmedia/bpm-types/dist/FilterBar';

export const useMalformed = () => {
  const [malformedObject, setMalformedObject] = useState<Error>();
  const [showMalformed, setShowMalformed] = useState(false);

  const exposedSetMalformed = useCallback(
    (error: string | Error | undefined) => {
      if (error) {
        setMalformedObject(typeof error === 'string' ? new Error(error) : error);
        setShowMalformed(true);
        let stillUndoing = true;
        setTimeout(() => {
          if (stillUndoing) {
            setShowMalformed(false);
          }
        }, 2000);
        return () => {
          stillUndoing = false;
        };
      }
      setShowMalformed(false);
      return undefined;
    },
    [setShowMalformed, setMalformedObject]
  );
  const dismissMalformed = useCallback(() => exposedSetMalformed(undefined), [exposedSetMalformed]);
  return {
    malformedObject,
    showMalformed,
    setShowMalformed,
    setMalformed: exposedSetMalformed,
    dismissMalformed
  };
};

// The basic FilterBar emits each search term expanded to `(dim1 is like t or dim2 is like t …)`;
// recover the terms for the server-side `tableFilter`. A term equal to a dim's name expands to
// `dim is true` for that dim instead, so fall back to the category label when a group has no
// `is like`. Terms are ANDed, so duplicates are dropped.
export const getTableFilterTerms = (tokens: Option[]): string[] => {
  const terms = new Set<string>();
  let groupTerm: string | undefined;
  let fallbackTerm: string | undefined;
  tokens.forEach((token, i) => {
    const prev = tokens[i - 1];
    if (token.type === FilterColumnType.OPEN_PAREN) {
      groupTerm = undefined;
      fallbackTerm = undefined;
    } else if (token.type === FilterColumnType.VAL && prev?.key === 'is like') {
      groupTerm ??= token.key ?? token.label;
    } else if (token.type === FilterColumnType.OP && token.key === 'is' && prev) {
      fallbackTerm ??= prev.label;
    } else if (token.type === FilterColumnType.CLOSE_PAREN) {
      const term = groupTerm ?? fallbackTerm;
      if (term) {
        terms.add(term);
      }
    }
  });
  return [...terms];
};

const tokenSignature = (tokens: Option[]): string =>
  JSON.stringify(tokens.map(({ type, key, label }) => [type, key, label]));

// `getTableFilterTerms` when the tokens are exactly a basic search over `options`; undefined for an
// advanced expression (e.g. `is not`), which a term list can't express. A term equal to a dim's
// name expands to `dim is true` rather than a substring match, so it is left client-side too.
export const getBasicTableFilterTerms = (
  tokens: Option[],
  options: string[] | Option[]
): string[] | undefined => {
  if (tokens.some(token => token.type === FilterColumnType.OP && token.key === 'is')) {
    return undefined;
  }
  const terms = getTableFilterTerms(tokens);
  const expanded = parseTokensBasicToAdvanced(
    terms.map(value => ({ value, label: value })),
    options
  );
  return tokenSignature(expanded) === tokenSignature(tokens) ? terms : undefined;
};
